import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { ParticipantLayout } from "../../components/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../components/ui/accordion";
import { toast } from "sonner";
import { 
  BookOpen, Play, FileText, CheckCircle, Clock, Download, ArrowLeft,
  Video, File, PenTool, Lightbulb, ExternalLink
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONTENT_TYPE_ICONS = {
  "video": Video,
  "document": FileText,
  "exercise": PenTool,
  "solution": Lightbulb,
  "download": Download
};

const CONTENT_TYPE_LABELS = {
  "video": "Video",
  "document": "Dokument",
  "exercise": "Übung",
  "solution": "Lösung",
  "download": "Download"
};

const CourseDetail = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeContent, setActiveContent] = useState(null);

  useEffect(() => {
    loadCourse();
  }, [courseId]);

  const loadCourse = async () => {
    try {
      const response = await axios.get(`${API}/courses/${courseId}`);
      setCourse(response.data);
      
      // Find first uncompleted content or first content
      if (response.data.modules?.length > 0) {
        for (const module of response.data.modules) {
          for (const content of module.content || []) {
            if (!content.completed) {
              setActiveContent(content);
              return;
            }
          }
        }
        // All completed, show first content
        if (response.data.modules[0]?.content?.length > 0) {
          setActiveContent(response.data.modules[0].content[0]);
        }
      }
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Kein Zugriff auf diesen Kurs");
        navigate("/lernplattform");
      } else {
        toast.error("Fehler beim Laden des Kurses");
      }
    } finally {
      setLoading(false);
    }
  };

  const markComplete = async (contentId) => {
    try {
      await axios.post(`${API}/courses/progress`, null, {
        params: { content_id: contentId, completed: true, progress_percent: 100 }
      });
      toast.success("Als abgeschlossen markiert");
      loadCourse();
    } catch (error) {
      toast.error("Fehler beim Speichern");
    }
  };

  const calculateProgress = () => {
    if (!course?.modules) return { completed: 0, total: 0, percent: 0 };
    let completed = 0;
    let total = 0;
    
    course.modules.forEach(module => {
      module.content?.forEach(content => {
        total++;
        if (content.completed) completed++;
      });
    });
    
    return {
      completed,
      total,
      percent: total > 0 ? Math.round(completed / total * 100) : 0
    };
  };

  const progress = calculateProgress();

  const ContentIcon = ({ type }) => {
    const Icon = CONTENT_TYPE_ICONS[type] || FileText;
    return <Icon className="h-4 w-4" />;
  };

  if (loading) {
    return (
      <ParticipantLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </ParticipantLayout>
    );
  }

  if (!course) {
    return (
      <ParticipantLayout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">Kurs nicht gefunden</p>
          <Button onClick={() => navigate("/lernplattform")} className="mt-4">
            Zurück zur Übersicht
          </Button>
        </div>
      </ParticipantLayout>
    );
  }

  return (
    <ParticipantLayout>
      <div className="space-y-6" data-testid="course-detail">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/lernplattform")}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">{course.title}</h1>
              <p className="text-muted-foreground">{course.category}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Fortschritt</p>
              <p className="font-bold text-lg">{progress.percent}%</p>
            </div>
            <div className="w-32">
              <Progress value={progress.percent} className="h-3" />
              <p className="text-xs text-muted-foreground mt-1">
                {progress.completed} / {progress.total}
              </p>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Content Viewer */}
          <div className="lg:col-span-2 space-y-4">
            {activeContent ? (
              <Card className="bg-card border-border">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <ContentIcon type={activeContent.content_type} />
                      <Badge variant="outline">
                        {CONTENT_TYPE_LABELS[activeContent.content_type]}
                      </Badge>
                    </div>
                    {activeContent.completed ? (
                      <Badge className="bg-green-500 text-white">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Abgeschlossen
                      </Badge>
                    ) : (
                      <Button size="sm" onClick={() => markComplete(activeContent.id)}>
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Als erledigt markieren
                      </Button>
                    )}
                  </div>
                  <CardTitle className="mt-2">{activeContent.title}</CardTitle>
                  {activeContent.duration_minutes && (
                    <CardDescription className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {activeContent.duration_minutes} Minuten
                    </CardDescription>
                  )}
                </CardHeader>
                <CardContent>
                  {/* Video Content */}
                  {activeContent.content_type === "video" && activeContent.content_url && (
                    <div className="aspect-video bg-black rounded-lg overflow-hidden">
                      {activeContent.content_url.includes("youtube") || activeContent.content_url.includes("youtu.be") ? (
                        <iframe
                          src={activeContent.content_url.replace("watch?v=", "embed/")}
                          className="w-full h-full"
                          allowFullScreen
                          title={activeContent.title}
                        />
                      ) : (
                        <video
                          src={activeContent.content_url}
                          controls
                          className="w-full h-full"
                        />
                      )}
                    </div>
                  )}
                  
                  {/* Document/Text Content */}
                  {(activeContent.content_type === "document" || activeContent.content_type === "exercise" || activeContent.content_type === "solution") && (
                    <div className="space-y-4">
                      {activeContent.content_text && (
                        <div className="prose prose-invert max-w-none">
                          <div dangerouslySetInnerHTML={{ __html: activeContent.content_text.replace(/\n/g, '<br/>') }} />
                        </div>
                      )}
                      {activeContent.content_url && (
                        <Button variant="outline" asChild>
                          <a href={activeContent.content_url} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="h-4 w-4 mr-2" />
                            Dokument öffnen
                          </a>
                        </Button>
                      )}
                    </div>
                  )}
                  
                  {/* Download Content */}
                  {activeContent.content_type === "download" && activeContent.content_url && (
                    <div className="flex items-center justify-center py-8">
                      <Button asChild>
                        <a href={activeContent.content_url} download>
                          <Download className="h-4 w-4 mr-2" />
                          Datei herunterladen
                        </a>
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-card border-border">
                <CardContent className="py-12 text-center text-muted-foreground">
                  <BookOpen className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Wählen Sie einen Inhalt aus der Übersicht</p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Course Outline Sidebar */}
          <div className="space-y-4">
            <Card className="bg-card border-border">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Kursinhalt</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {course.modules?.length > 0 ? (
                  <Accordion type="multiple" className="w-full" defaultValue={course.modules.map(m => m.id)}>
                    {course.modules.map((module, moduleIndex) => (
                      <AccordionItem key={module.id} value={module.id} className="border-b border-border">
                        <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-secondary/30">
                          <div className="flex items-center gap-2 text-left">
                            <span className="text-xs text-muted-foreground w-6">
                              {moduleIndex + 1}.
                            </span>
                            <span className="font-medium text-sm">{module.title}</span>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="pb-0">
                          <div className="space-y-1 px-2 pb-2">
                            {module.content?.map((content, contentIndex) => (
                              <button
                                key={content.id}
                                onClick={() => setActiveContent(content)}
                                className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-left text-sm transition-colors ${
                                  activeContent?.id === content.id
                                    ? "bg-primary/10 text-primary"
                                    : "hover:bg-secondary/50"
                                }`}
                              >
                                {content.completed ? (
                                  <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                                ) : (
                                  <ContentIcon type={content.content_type} />
                                )}
                                <span className="truncate">{content.title}</span>
                                {content.duration_minutes && (
                                  <span className="text-xs text-muted-foreground ml-auto">
                                    {content.duration_minutes}m
                                  </span>
                                )}
                              </button>
                            ))}
                            {(!module.content || module.content.length === 0) && (
                              <p className="text-xs text-muted-foreground px-3 py-2">
                                Keine Inhalte
                              </p>
                            )}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                ) : (
                  <div className="p-4 text-center text-muted-foreground">
                    <p className="text-sm">Noch keine Module</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Course Description */}
            {course.description && (
              <Card className="bg-card border-border">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Beschreibung</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">{course.description}</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </ParticipantLayout>
  );
};

export default CourseDetail;
